import { createContext, useContext } from "react";
import { decisionOn, unlisted, type AccessRefusal, type Decision } from "../api/access/decisions";
import type { PermittedAction } from "../api/permittedActions";
import type { ProjectOverview, ProjectSummary } from "../api/projectsApi";
import type { LoadState } from "../shared/loading/useLoad";

/** Where the chosen project is kept in the browser. */
export const SELECTED_PROJECT_STORAGE_KEY = "dmw.selectedProject";

/**
 * The project the user works in. Corpora and all other content belong to one project. The
 * choice is kept in the browser, not in addresses (Q-37 is open).
 */
export interface ProjectContextValue {
  /** The projects the user can see. */
  readonly projects: LoadState<readonly ProjectSummary[]>;
  /** `null` while no project is chosen (or the chosen one is not visible any more). */
  readonly selectedProjectId: string | null;
  /** The chosen project and what the user may do in it; `null` while none is chosen. */
  readonly selectedProject: LoadState<ProjectOverview> | null;
  readonly selectProject: (projectId: string) => void;
  /** Whether a visible project can be the chosen one: the DMW core only for administrators (F62). */
  readonly mayChoose: (project: ProjectSummary) => boolean;
  readonly reloadProjects: () => void;
  /** Reads the chosen project's permitted actions again, keeping the current ones meanwhile,
   * after a change to roles, groups or rules. */
  readonly refreshSelectedProject: () => void;
}

export const ProjectContext = createContext<ProjectContextValue | null>(null);

export function useProjects(): ProjectContextValue {
  const value = useContext(ProjectContext);
  if (value === null) {
    throw new Error("useProjects must be used inside ProjectProvider.");
  }
  return value;
}

/** The user's decision on `action` in the chosen project (SOT-02); until it has loaded, nothing is offered. */
export function chosenProjectDecision(context: ProjectContextValue, action: PermittedAction): Decision<AccessRefusal> {
  const chosen = context.selectedProject;
  return chosen?.status === "loaded" ? decisionOn(chosen.value.decisions, action, chosen.value.project.projectId) : unlisted(context.selectedProjectId);
}

/** The user's decision on `action` in `projectId`, which a view beside the chosen project may show: known for the
 * chosen project only, so in any other it isn't offered. */
export function projectDecision(context: ProjectContextValue, projectId: string, action: PermittedAction): Decision<AccessRefusal> {
  return context.selectedProjectId === projectId ? chosenProjectDecision(context, action) : unlisted(projectId);
}
